/**
 * React хук для работы с WebSocket клиентом
 */

import { useEffect, useCallback, useRef, useState } from 'react';

import { getBusSocket, BusSocket } from './client';
import { logError } from '../config/constants';
import { AnyEventHandler, EventKind } from '../model/envelope';

// Канал может доставлять как Envelope, так и "сырой" payload (в зависимости от сервера/роутера).
export type ChannelHandler = (payload: unknown) => void;

// Глобальный socket ref для предотвращения создания множественных экземпляров
const globalSocketRef = { current: null as BusSocket | null };

const getGlobalSocket = () => {
  if (!globalSocketRef.current) {
    globalSocketRef.current = getBusSocket();
  }
  return globalSocketRef.current;
};

interface UseBusSocketOptions {
  autoConnect?: boolean;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: string) => void;
}

/**
 * Основной хук для работы с WebSocket
 */
export const useBusSocket = (options: UseBusSocketOptions = {}) => {
  const {
    autoConnect = true,
    onConnected,
    onDisconnected,
    onError,
  } = options;

  const socketRef = useRef<BusSocket>(getGlobalSocket());
  const unsubscribeRefs = useRef<Set<() => void>>(new Set());
  
  // Стабилизируем колбэки через ref, чтобы не пересоздавать подписки при каждом рендере
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);
  const onErrorRef = useRef(onError);
  
  useEffect(() => {
    onConnectedRef.current = onConnected;
    onDisconnectedRef.current = onDisconnected;
    onErrorRef.current = onError;
  }, [onConnected, onDisconnected, onError]);

  useEffect(() => {
    if (autoConnect) {
      socketRef.current.connect();
    }
    const unsubscribes = unsubscribeRefs.current;

    // Setup event listeners с обертками, которые используют актуальные колбэки из ref
    if (onConnectedRef.current) {
      const wrappedOnConnected = () => {
        onConnectedRef.current?.();
      };
      unsubscribes.add(socketRef.current.onConnected(wrappedOnConnected));
    }

    if (onDisconnectedRef.current) {
      const wrappedOnDisconnected = () => {
        onDisconnectedRef.current?.();
      };
      unsubscribes.add(socketRef.current.onDisconnected(wrappedOnDisconnected));
    }

    if (onErrorRef.current) {
      const wrappedOnError = (error: string) => {
        onErrorRef.current?.(error);
      };
      unsubscribes.add(socketRef.current.onError(wrappedOnError));
    }

    return () => {
      // Cleanup all subscriptions
      unsubscribes.forEach((unsubscribe) => unsubscribe());
      unsubscribes.clear();
    };
  }, [autoConnect]);

  const subscribe = useCallback((
    channel: string,
    handler: ChannelHandler,
    options?: { snapshot?: boolean; replayBufferSize?: number }
  ) => {
    const unsubscribe = socketRef.current.subscribe(channel, handler, options);
    unsubscribeRefs.current.add(unsubscribe);
    return () => {
      unsubscribe();
      unsubscribeRefs.current.delete(unsubscribe);
    };
  }, []);

  const subscribeByType = useCallback((
    eventKind: EventKind,
    handler: AnyEventHandler,
    options?: { snapshot?: boolean }
  ) => {
    const unsubscribe = socketRef.current.subscribeByType(eventKind, handler, options);
    unsubscribeRefs.current.add(unsubscribe);
    return () => {
      unsubscribe();
      unsubscribeRefs.current.delete(unsubscribe);
    };
  }, []);

  const unsubscribe = useCallback((channel: string, handler?: ChannelHandler) => {
    socketRef.current.unsubscribe(channel, handler);
    // Note: unsubscribeRefs очищаются автоматически при unmount компонента
    // Для ручной отписки через subscribe/subscribeByType используется возвращаемая функция
  }, []);

  const getStatus = useCallback(() => {
    return socketRef.current.getStatus();
  }, []);

  const connect = useCallback(() => {
    socketRef.current.connect();
  }, []);

  const disconnect = useCallback(() => {
    socketRef.current.disconnect();
  }, []);

  return {
    subscribe,
    subscribeByType,
    unsubscribe,
    getStatus,
    connect,
    disconnect,
  };
};

/**
 * Хук для подписки на определенный тип событий
 */
export const useBusEvents = (
  eventKind: EventKind,
  handler: AnyEventHandler,
  options: {
    snapshot?: boolean;
    enabled?: boolean;
  } = {}
) => {
  const { snapshot = true, enabled = true } = options;

  // Делаем handler стабильным между рендерами, чтобы не пересоздавать подписку
  const handlerRef = useRef<AnyEventHandler>(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    try {
      const socket = getGlobalSocket();
      // Оборачиваем, чтобы всегда вызывать актуальный handler из ref
      const wrappedHandler: AnyEventHandler = (event) => {
        try {
          handlerRef.current?.(event);
        } catch (e) {
          logError('Event handler error', e);
        }
      };
      const unsubscribe = socket.subscribeByType(eventKind, wrappedHandler, { snapshot });

      return unsubscribe;
    } catch (error) {
      logError(`Failed to subscribe to event ${eventKind}`, error);
      return () => {}; // Return no-op cleanup function
    }
  }, [eventKind, snapshot, enabled]);
};

/**
 * Хук для отслеживания статуса WebSocket соединения.
 * Обновляет isConnected при подключении/отключении и по таймеру (fallback).
 */
export const useWebSocketStatus = () => {
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const socket = getGlobalSocket();

    const updateStatus = () => {
      try {
        const status = socket.getStatus();
        setIsConnected(status.isConnected);
      } catch {
        setIsConnected(false);
      }
    };

    updateStatus();

    const unsubConnected = socket.onConnected(updateStatus);
    const unsubDisconnected = socket.onDisconnected(updateStatus);
    const interval = setInterval(updateStatus, 5000);

    return () => {
      unsubConnected();
      unsubDisconnected();
      clearInterval(interval);
    };
  }, []);

  return { isConnected };
};

/**
 * Хук для подписки на каналы
 */
export const useBusChannel = (
  channel: string,
  handler: ChannelHandler,
  options: {
    snapshot?: boolean;
    replayBufferSize?: number;
    enabled?: boolean;
  } = {}
) => {
  const { snapshot = true, replayBufferSize = 50, enabled = true } = options;

  // Делаем handler стабильным между рендерами, чтобы не пересоздавать подписку
  const handlerRef = useRef<ChannelHandler>(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    try {
      const socket = getGlobalSocket();
      // Оборачиваем, чтобы всегда вызывать актуальный handler из ref
      const wrappedHandler: ChannelHandler = (event: unknown) => {
        try {
          handlerRef.current?.(event);
        } catch (e) {
          logError('Channel handler error', e);
        }
      };
      const unsubscribe = socket.subscribe(channel, wrappedHandler, { snapshot, replayBufferSize });

      return () => {
        unsubscribe();
      };
    } catch (error) {
      logError(`Failed to subscribe to channel ${channel}`, error);
      return () => {}; // Return no-op cleanup function
    }
  }, [channel, snapshot, replayBufferSize, enabled]);
};
